import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, LMP_CONSOLIDATED_TABLE_SUFFIX, POPOUT_MESSAGE } from '../Constants';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { ChartProps } from '../Interfaces/Charts';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';

export default function LMPConsolidatedTable(props: ChartProps) {
	// ***************************************************************************************************************************************************
	// Note: Removed unused columnWidths useMemo and associated DOM manipulation logic.
	// *** Custom Block: the following two defintions are custom to each table this code can produce,
	//      they provide the location for the data and the names of the columns to display
	const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${LMP_CONSOLIDATED_TABLE_SUFFIX}`;
	const download_filename = 'LMPConsolidatedTable';
	const popout_path = '/charts/LmpConsolidatedTable';
	const modalHeader: React.ReactNode = <span>LMP Consolidated Table</span>;

	interface IConsolidatedRow {
		FiveMinName: string;
		FiveMinRegion: string;
		FiveMinLMP: string;
		FiveMinMCC: string;
		FiveMinMLC: string;
		HourlyName: string;
		HourlyRegion: string;
		HourlyLMP: string;
		HourlyMCC: string;
		HourlyMLC: string;
		ExAnteName: string;
		ExAnteRegion: string;
		ExAnteLMP: string;
		ExAnteMCC: string;
		ExAnteMLC: string;
		ExPostName: string;
		ExPostRegion: string;
		ExPostLMP: string;
		ExPostMCC: string;
		ExPostMLC: string;
	}

	//
	function extractRowDataAndIntervalString(json: any) {
		const FiveMinLmpRowData = json.LMPData?.FiveMinLMP?.PricingNode ?? [];
		const HourlyIntegratedLMPRowData = json.LMPData?.HourlyIntegratedLMP?.PricingNode ?? [];
		const DayAheadExAnteLMPRowData = json.LMPData?.DayAheadExAnteLMP?.PricingNode ?? [];
		const DayAheadExPostLMPRowData = json.LMPData?.DayAheadExPostLMP?.PricingNode ?? [];

		const FiveMinHourAndMin = json.LMPData?.FiveMinLMP?.HourAndMin ?? '';
		const HourlyHourAndMin = json.LMPData?.HourlyIntegratedLMP?.HourAndMin ?? '';
		const ExAnteHourAndMin = json.LMPData?.DayAheadExAnteLMP?.HourAndMin ?? '';
		const ExPostHourAndMin = json.LMPData?.DayAheadExPostLMP?.HourAndMin ?? '';

		const allNames = new Set<string>();
		FiveMinLmpRowData.forEach((n: any) => allNames.add(n.name));
		HourlyIntegratedLMPRowData.forEach((n: any) => allNames.add(n.name));
		DayAheadExAnteLMPRowData.forEach((n: any) => allNames.add(n.name));
		DayAheadExPostLMPRowData.forEach((n: any) => allNames.add(n.name));

		const rows: IConsolidatedRow[] = Array.from(allNames).map((name) => {
			const fiveMin = FiveMinLmpRowData.find((n: any) => n.name === name) || {};
			const hourly = HourlyIntegratedLMPRowData.find((n: any) => n.name === name) || {};
			const exAnte = DayAheadExAnteLMPRowData.find((n: any) => n.name === name) || {};
			const exPost = DayAheadExPostLMPRowData.find((n: any) => n.name === name) || {};

			return {
				FiveMinName: fiveMin.name ?? '',
				FiveMinRegion: fiveMin.region ?? '',
				FiveMinLMP: fiveMin.LMP ?? '',
				FiveMinMCC: fiveMin.MCC ?? '',
				FiveMinMLC: fiveMin.MLC ?? '',
				HourlyName: hourly.name ?? '',
				HourlyRegion: hourly.region ?? '',
				HourlyLMP: hourly.LMP ?? '',
				HourlyMCC: hourly.MCC ?? '',
				HourlyMLC: hourly.MLC ?? '',
				ExAnteName: exAnte.name ?? '',
				ExAnteRegion: exAnte.region ?? '',
				ExAnteLMP: exAnte.LMP ?? '',
				ExAnteMCC: exAnte.MCC ?? '',
				ExAnteMLC: exAnte.MLC ?? '',
				ExPostName: exPost.name ?? '',
				ExPostRegion: exPost.region ?? '',
				ExPostLMP: exPost.LMP ?? '',
				ExPostMCC: exPost.MCC ?? '',
				ExPostMLC: exPost.MLC ?? ''
			};
		});

		const intervalString = json.LMPData?.RefId ?? '';

		return {
			rowData: rows,
			dataInterval: intervalString,
			FiveMinHourAndMin,
			HourlyHourAndMin,
			ExAnteHourAndMin,
			ExPostHourAndMin
		};
	}

	const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
		return (
			<>
				<span>
					The consolidated LMP Table provides a real time comparison of the market prices for the Day Ahead and Real Time markets. All values here are sourced from the
					Market User Interface and should be considered preliminary. The LMP Table data is automatically refreshed every five minutes from the source. You may search the
					LMP Table for a particular node name or character sequence. However, wild card search is not available.
				</span>
				<br />
				<br /> <span>This table is updated every 5 minutes.</span>
			</>
		);
	}, []);
	// ***************************************************************************************************************************************************

	const { buttonClicked } = props;
	const [showModal, setShowModal] = useState(false);
	const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);

	useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );
        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
				const newWindow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
				if (newWindow) newWindow.opener = null;
                break;
            default:
                break;
        }
	}, [buttonClicked, api_url, descriptionBody]);

	const useFetchJson = <T extends IConsolidatedRow>(url: string) => {
		const [data, setData] = useState<T[]>();
		const [loading, setLoading] = useState(false);
		const [dataInterval, setDataInterval] = useState<string>('');
		const [error, setError] = useState<string>('');
		const [FiveMinHourAndMin, setFiveMinHourAndMin] = useState<string>('');
		const [HourlyHourAndMin, setHourlyHourAndMin] = useState<string>('');
		const [ExAnteHourAndMin, setExAnteHourAndMin] = useState<string>('');
		const [ExPostHourAndMin, setExPostHourAndMin] = useState<string>('');

		// ***************************************************************************************************************************************************
		// One-time random refresh interval between 4m50s (290000 ms) and 5m10s (310000 ms)
		const REFRESH_MIN_MS = 290_000;
		const REFRESH_MAX_MS = 310_000;
		const refreshIntervalRef = useRef<number>(
			Math.floor(REFRESH_MIN_MS + Math.random() * (REFRESH_MAX_MS - REFRESH_MIN_MS))
		);
		// console.debug('LMP Consolidated refresh interval (ms):', refreshIntervalRef.current);
		// ***************************************************************************************************************************************************

		useEffect(() => {
             let refreshIntervalId: NodeJS.Timeout;
             const fetchData = async () => {
				setLoading(true);
				setError(''); // Reset error state before new fetch
				try {
					const response = await fetch(url);
					if (!response.ok) throw new Error(`Request failed: ${response.status}`);
					const json = await response.json();
					// raw JSON retained only in memory; no separate state needed
					const { rowData, dataInterval, FiveMinHourAndMin, HourlyHourAndMin, ExAnteHourAndMin, ExPostHourAndMin } = extractRowDataAndIntervalString(json);
					setDataInterval(dataInterval);
					setFiveMinHourAndMin(FiveMinHourAndMin);
					setHourlyHourAndMin(HourlyHourAndMin);
					setExAnteHourAndMin(ExAnteHourAndMin);
					setExPostHourAndMin(ExPostHourAndMin);
					setData(rowData as T[]);
				} catch {
                    setData([]); // On error, set data to empty array
                    setError('Data Connection Failed'); // Set error message
                } finally {
                    setLoading(false);
                }
            };
			fetchData();
			refreshIntervalId = setInterval(fetchData, refreshIntervalRef.current);
             return () => clearInterval(refreshIntervalId);
         }, [url]);

		return { data, loading, dataInterval, error, FiveMinHourAndMin, HourlyHourAndMin, ExAnteHourAndMin, ExPostHourAndMin };
	};

	const { data, loading, dataInterval, error, FiveMinHourAndMin, HourlyHourAndMin, ExAnteHourAndMin, ExPostHourAndMin } = useFetchJson<IConsolidatedRow>(api_url);

	const [regionFilter, setRegionFilter] = useState<string>('All');
	const [searchText, setSearchText] = useState<string>('');
	const [sort, setSort] = useState<Array<SortDescriptor>>([]);
	const handleSortChange = useCallback((e: any) => setSort(e.sort), []);

	const filteredData = useMemo(() => {
		if (!data) return [];
		let result = data;
		if (regionFilter !== 'All') {
			result = result.filter((row) => (row.FiveMinRegion ?? '').toLowerCase() === regionFilter.toLowerCase());
		}
		if (searchText.trim() !== '') {
			const search = searchText.trim().toLowerCase();
			result = result.filter((row) => (row.FiveMinName ?? '').toLowerCase().includes(search));
		}

		// Apply sorting if sort descriptors exist
		if (sort.length > 0) {
			result = orderBy(result, sort);
		}

		return result;
	}, [data, regionFilter, searchText, sort]);

	if (loading) {
		return <div className='loading-and-error-box'>Loading...</div>;
	}
	if (error) {
		return <div className='loading-and-error-box'>{error}</div>;
	}
	if (!data || data.length === 0) {
		return <div className='loading-and-error-box'>No Data Available</div>;
	}

	return (
		<>
			<div className='full-width-grid-wrapper' style={{ maxHeight: '800px', overflow: 'hidden' }}>
				<VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
				<div
					id='TopRow'
					style={{
						marginBottom: '12px',
						display: 'flex',
						flexDirection: 'row', // Default to row layout
						flexWrap: 'wrap', // Allow items to wrap
						justifyContent: 'space-between',
						gap: '16px',
						alignItems: 'center',
						width: '100%'
					}}
					className='top-row-container' // Add a class for media query targeting
				>
					<div className='interval-line' style={{ fontWeight: 'bold' }}>
						{dataInterval}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<span style={{ fontWeight: 'bold' }}>Search:</span>
						<input
							type='text'
							placeholder='Location'
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', marginRight: '16px' }}
						/>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<span style={{ fontWeight: 'bold' }}>Region Filter:</span>
						<label>
							<input type='radio' name='region' value='All' checked={regionFilter === 'All'} onChange={() => setRegionFilter('All')} />
							&nbsp;All
						</label>
						<label>
							<input type='radio' name='region' value='North' checked={regionFilter === 'North'} onChange={() => setRegionFilter('North')} />
							&nbsp;North
						</label>
						<label>
							<input type='radio' name='region' value='Central' checked={regionFilter === 'Central'} onChange={() => setRegionFilter('Central')} />
							&nbsp;Central
						</label>
						<label>
							<input type='radio' name='region' value='South' checked={regionFilter === 'South'} onChange={() => setRegionFilter('South')} />
							&nbsp;South
						</label>
					</div>
				</div>

				<Grid
					data={filteredData}
					style={{ width: '100%', height: 'calc(800px - 80px)' }}
					scrollable='scrollable'
					resizable
					sortable
					sort={sort}
					onSortChange={handleSortChange}>
					{/* Location column */}
					<Column field='FiveMinName' title='Location' width='180px' />

					{/* Hourly Day-Ahead Ex Ante group */}
					<Column title={`Hourly Day-Ahead Ex Ante -- ${ExAnteHourAndMin}`}>
						<Column field='ExAnteLMP' title='LMP' className='k-text-end' />
						<Column field='ExAnteMLC' title='MLC' className='k-text-end' />
						<Column field='ExAnteMCC' title='MCC' className='k-text-end mcc-red-text' />
					</Column>

					{/* Hourly Day-Ahead Ex Post group */}
					<Column title={`Hourly Day-Ahead Ex Post -- ${ExPostHourAndMin}`}>
						<Column field='ExPostLMP' title='LMP' className='k-text-end' />
						<Column field='ExPostMLC' title='MLC' className='k-text-end' />
						<Column field='ExPostMCC' title='MCC' className='k-text-end mcc-red-text' />
					</Column>

					{/* Five Minute Real-Time group */}
					<Column title={`Five Minute Real-Time -- ${FiveMinHourAndMin}`}>
						<Column field='FiveMinLMP' title='LMP' className='k-text-end' />
						<Column field='FiveMinMLC' title='MLC' className='k-text-end' />
						<Column field='FiveMinMCC' title='MCC' className='k-text-end mcc-red-text' />
					</Column>

					{/* Last Hour Estimated group */}
					<Column title={`Last Hour Estimated -- ${HourlyHourAndMin}`}>
						<Column field='HourlyLMP' title='LMP' className='k-text-end' />
						<Column field='HourlyMLC' title='MLC' className='k-text-end' />
						<Column field='HourlyMCC' title='MCC' className='k-text-end mcc-red-text' />
					</Column>
				</Grid>
			</div>
		</>
	);
}
